// shared-lib/cabinet-runs/rollOuts.js
//
// Roll-out shelves: pull-out trays inside a base cabinet, behind its doors
// (design 2026-10-25, #2907). A lower cabinet carries `rollOuts: 1..3`;
// absent means none. A run-owned cabinet gets it from a width-less lower-tier
// bay override (`stampRollOuts`, called by `fillRun.emitBay`), so a wall drag
// or a regenerate keeps it; a cabinet no run owns carries it itself.
//
// The one rule for "may this box have roll-outs, and if not, why" — the fill,
// the write boundary, the panels, the renderer and the estimate all ask it,
// the way `isUnderCabinetLightEligible` is asked for the LED strip.
//
// Pure.

const { CABINET } = require('./cabinetFrontSpecs');
const { cabinetFrontLayout } = require('./cabinetFrontLayout');
const { cabinetRollOutSlots } = require('./cabinetInterior');
const { isDoorlessCabinet } = require('./doorStyles');
const { familySizes, unitsOf } = require('./vendorSizes');
const { familyById } = require('./families');
const { vendorOf } = require('./glassUppers');
const {
  objectIsPlaceholder,
} = require('../fixture-types/productRepresentation');

const IN = 0.0254;
const ROLL_OUT_MAX = 3;
/** The vendor catalog kind a roll-out tray is listed as (Venus RD). */
const ROLL_OUT_TRAY_KIND = 'rollout-tray';
const CORNER_TYPES = new Set(['corner-cabinet', 'corner-fill-cabinet']);
const SINK_COLUMNS = new Set(['sink-front', 'farmhouse-front']);
// Tolerance on a box's width and depth against the minimums: a 12" box
// stored as 0.3048 m reads 11.99999… inches.
const SIZE_EPS_IN = 1e-6;

const said = (n) => `${+n.toFixed(2)}"`;

/** 1, 2 or 3 — or null for anything else (none). */
function normalizeRollOuts(n) {
  return Number.isInteger(n) && n >= 1 && n <= ROLL_OUT_MAX ? n : null;
}

/** The box's width / height / depth in metres, or null with no size. */
function sizeOf(box) {
  const d = box?.dimensions || {};
  const width = d.x ?? d.width;
  const height = d.y ?? d.height;
  const depth = d.z ?? d.depth;
  const ok = (v) => typeof v === 'number' && Number.isFinite(v) && v > 0;
  return ok(width) && ok(height) && ok(depth) ? { width, height, depth } : null;
}

/** What the scan built: a scanned object, or a box of a scanned run. */
const isScanBuilt = (box) =>
  box?.scanned === true || String(box?.runId || '').startsWith('scanrun_');

const isCornerUnit = (box) =>
  CORNER_TYPES.has(box?.type) || !!box?.category?.blindCorner;

const columnTypes = (box) =>
  Array.isArray(box?.columns) ? box.columns.map((c) => c?.type) : [];

// A vanity is written as a plain door box with a sink COUNT
// (`assembleVanity`), its basin filling the box — a sink base all the same.
const isSinkBase = (box) =>
  !!box?.category?.sinkBase ||
  Number(box?.sinkCount) > 0 ||
  columnTypes(box).some((t) => SINK_COLUMNS.has(t));

// A column a tray can stand behind: doors, with or without a drawer over
// them (a column with no type is a door bank).
const DOOR_COLUMNS = new Set([undefined, 'doors', 'drawer-doors']);
const hasDoorColumn = (box) => {
  const types = columnTypes(box);
  return types.length === 0 || types.some((t) => DOOR_COLUMNS.has(t));
};

// A drawer base, or a box of columns none of which has doors — a column of
// doors beside a drawer stack still takes trays behind its doors.
const isDrawerBase = (box) =>
  !!box?.category?.drawer ||
  (!hasDoorColumn(box) && columnTypes(box).includes('drawers'));

/** A lower whose body (above the toe kick) passes a tall unit's. */
const isTallBox = (box, size) =>
  !!box?.category?.tall ||
  size.height - CABINET.toeKickHeight > CABINET.tallBodyMin;

/**
 * Why a size-catalog family makes no tray for this box, or null when it
 * makes one (or has no catalog — any width then takes a tray).
 */
function vendorTrayReason(family, size) {
  if (!familySizes(family)) return null;
  const trays = unitsOf(family, ROLL_OUT_TRAY_KIND);
  if (!trays.length) return `${vendorOf(family)} makes no roll-out trays`;
  const widthIn = size.width / IN;
  const tray = trays.find((u) => Math.abs(u.w - widthIn) < 0.1);
  if (!tray) {
    const widths = [...new Set(trays.map((u) => u.w))].sort((a, b) => a - b);
    return `${vendorOf(family)} makes roll-out trays for bases ${said(
      widths[0]
    )} to ${said(widths[widths.length - 1])} wide`;
  }
  // A tray is made for a base of one depth (Venus's RD: its 24" bases).
  if (tray.baseDepth && size.depth / IN < tray.baseDepth - 0.1)
    return `${vendorOf(family)} makes roll-out trays for ${said(
      tray.baseDepth
    )} deep bases`;
  return null;
}

/**
 * Why `box` (a cabinet as built, or a probe) may not have roll-outs in
 * `family` (its run's cabinet line, or null), or null when it may. The first
 * that applies, in the order a person would ask: what kind of cabinet it is,
 * whether the scan built it, its front, its size, and its line.
 */
function rollOutUnavailableReason(family, box) {
  if (isCornerUnit(box)) return 'Corner units take no roll-outs';
  const size = sizeOf(box);
  if (!size || box.type !== 'lower-cabinet')
    return 'Roll-outs go in base cabinets';
  if (!objectIsPlaceholder(box)) return 'A catalog cabinet takes no roll-outs';
  if (isScanBuilt(box)) return 'A scanned cabinet stays as scanned';
  if (isDoorlessCabinet(box))
    return 'A cabinet with no doors takes no roll-outs';
  if (isSinkBase(box)) return 'A sink base takes no roll-outs';
  if (isDrawerBase(box)) return 'A drawer base takes no roll-outs';
  if (!hasDoorColumn(box)) return 'A cabinet with no doors takes no roll-outs';
  if (isTallBox(box, size)) return 'Tall units take no roll-outs';
  if (size.width / IN < CABINET.rollOutMinWidth / IN - SIZE_EPS_IN)
    return `Too narrow for a roll-out (${said(
      CABINET.rollOutMinWidth / IN
    )} minimum)`;
  if (size.depth / IN < CABINET.rollOutMinDepth / IN - SIZE_EPS_IN)
    return `Too shallow for a roll-out (${said(
      CABINET.rollOutMinDepth / IN
    )} minimum)`;
  return vendorTrayReason(family, size);
}

/** May this box have roll-outs (no line asked)? */
const isRollOutEligible = (box) => rollOutUnavailableReason(null, box) == null;

/**
 * The roll-outs a cabinet is DRAWN and BILLED with, before the slot cap of
 * its front (`cabinetRollOutSlots`): its own count on an eligible box, else 0.
 */
function rollOutCountOf(obj) {
  const n = normalizeRollOuts(obj?.rollOuts);
  return n && isRollOutEligible(obj) ? n : 0;
}

/**
 * A lower box's front, laid out as its renderer lays it out — the frame the
 * tray slots are measured in. Null with no size.
 */
function lowerLayoutOf(box) {
  const size = sizeOf(box);
  if (!size) return null;
  return cabinetFrontLayout(
    { ...size, handleAtBottom: box.dimensions?.handleAtBottom === true },
    { columns: box.columns, remainderAlign: box.remainderAlign }
  );
}

/**
 * How many trays the front of `box` has room for in a door column (the most
 * any column holds, up to `ROLL_OUT_MAX`): fewer under a drawer + doors
 * drawer or in a short base, 0 with no size. What the panel greys its larger
 * chips by — `cabinetRollOutSlots` caps the trays it draws and bills the
 * same way.
 */
function rollOutRoomFor(box) {
  return Math.max(0, ...traysPerColumn(box).values());
}

/** Column index → trays its doors have room for, for every door column. */
function traysPerColumn(box) {
  const layout = lowerLayoutOf(box);
  const perColumn = new Map();
  if (!layout) return perColumn;
  for (const s of cabinetRollOutSlots(layout, ROLL_OUT_MAX)) {
    perColumn.set(s.columnIndex, (perColumn.get(s.columnIndex) ?? 0) + 1);
  }
  return perColumn;
}

/**
 * How many door columns of `box` hold trays. The count a cabinet carries is
 * PER DOOR COLUMN (each column's doors open on its own trays), so a box of
 * two door columns set to 2 draws and bills 4 — the panel says so.
 */
const rollOutColumnsOf = (box) => traysPerColumn(box).size;

/**
 * The line a cabinet NO RUN owns is judged in: its own `runFamily` (a vendor
 * line's box kept after its run went), else none. A run's cabinet is judged
 * in its run's line by whoever holds the run (the fill, the bay write).
 */
const rollOutFamilyOf = (box) =>
  !box?.runId && box?.runFamily ? familyById(box.runFamily) || null : null;

/**
 * Put the roll-outs the governing bay record asks for on a cabinet the fill
 * just built — or take them off. `delete`, never `= undefined`: Firestore
 * rejects an undefined value anywhere in a document. Mutates, like the
 * appearance pass.
 */
function stampRollOuts(cabinet, record, family) {
  const n = normalizeRollOuts(record?.rollOuts);
  if (n && rollOutUnavailableReason(family, cabinet) == null) {
    cabinet.rollOuts = n;
  } else {
    delete cabinet.rollOuts;
  }
}

module.exports = {
  ROLL_OUT_MAX,
  ROLL_OUT_TRAY_KIND,
  normalizeRollOuts,
  rollOutUnavailableReason,
  isRollOutEligible,
  rollOutCountOf,
  rollOutRoomFor,
  rollOutColumnsOf,
  rollOutFamilyOf,
  lowerLayoutOf,
  stampRollOuts,
};
